import type { HealthcareService, Reference } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import { Field } from '../../components/Field';
import type { FhirView } from '../../core/view-contract';

import { HealthcareServiceEditView } from './HealthcareServiceEditView';
import {
  APPOINTMENT_TYPE_LABELS,
  appointmentTypeCode,
  schedulingParameters,
  schedulingSummary,
} from './scheduling';

/**
 * The exported HealthcareService component — a bookable service offering:
 * identity (name, description), an optional appointment-type label, and the
 * eluve scheduling parameters.
 */
export const HealthcareServiceView: FhirView<HealthcareService> = (props) => {
  if (props.ctx.inList) return <ServiceRowView {...props} />;
  if (props.ctx.mode === 'edit')
    return <HealthcareServiceEditView {...props} />;
  return <ServiceReadView {...props} />;
};

/**
 * Names the targets of a repeating Reference field.
 *
 * A path whose last segment is a repeating element resolves to the whole
 * array at runtime, while the path type models one element (arrays unwrap on
 * the *next* segment) — hence the normalize. Each reference carries a
 * `display` snapshot written at selection time, so the names read without
 * resolving the targets.
 */
function referenceNames(value: unknown): string {
  const refs: Reference[] = Array.isArray(value)
    ? (value as Reference[])
    : value
      ? [value as Reference]
      : [];
  return refs
    .map((ref) => ref.display ?? ref.reference)
    .filter(Boolean)
    .join(', ');
}

function ClassificationTag({ data }: { data: HealthcareService }) {
  const code = appointmentTypeCode(data);
  if (!code) return null;
  return (
    <Tag
      pill
      subtle
      size="xs"
      color="gray"
      label={APPOINTMENT_TYPE_LABELS[code] ?? code}
    />
  );
}

const ServiceRowView: FhirView<HealthcareService> = ({ data }) => {
  const summary = schedulingSummary(schedulingParameters(data));
  return (
    <div data-view="healthcare-service-row" className="py-2">
      <Stack direction="horizontal" gap={2} align="center" className="min-w-0">
        <Text inline size="sm" weight="regular" className="truncate">
          {data.name ?? 'Unnamed service'}
        </Text>
        <ClassificationTag data={data} />
        {data.active === false && <FhirStatusTag status="inactive" />}
      </Stack>
      {summary && (
        <Text size="xs" color="tertiary" className="truncate">
          {summary}
        </Text>
      )}
    </div>
  );
};

const ServiceReadView: FhirView<HealthcareService> = ({ data, ctx }) => {
  const summary = schedulingSummary(schedulingParameters(data));
  return (
    <div
      data-view="healthcare-service-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          fullWidth={false}
          className="min-w-0"
        >
          <Text inline size="sm" weight="semibold" className="truncate">
            {data.name ?? 'Unnamed service'}
          </Text>
          <ClassificationTag data={data} />
        </Stack>
        <FhirStatusTag status={data.active === false ? 'inactive' : 'active'} />
      </Stack>
      <Grid columns={1} className="mt-3 gap-x-6 gap-y-3 sm:grid-cols-2">
        <Field label="Description" data={data} path={['comment']} />
        <Field
          label="Locations"
          data={data}
          path={['location']}
          render={(value) => referenceNames(value)}
        />
      </Grid>
      {summary && (
        <Text size="xs" color="tertiary" className="mt-3">
          Scheduling: {summary}
        </Text>
      )}
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
